// src/entities/authorComposer/components/common/authorStatus.ts
/**
 * Lo que la aplicación muestra de un autor más allá de sus campos: qué datos
 * validó con su DNI (lo escribe el server, nunca el formulario) y si está en
 * condiciones de entrar en una documentación (la misma cuenta que la
 * validación de la generación, vía authorComposerDocumentationIssues).
 *
 * Un listado muestra muchos autores a la vez: los pedidos de una misma vuelta
 * se juntan en una sola consulta. No hay caché: cada pantalla que se monta
 * pregunta de nuevo, así lo que se ve es lo de ahora.
 */
import { useEffect, useState } from "react";
import { restFetch } from "../../../../api/apiClient";
import { diaDeCalendario } from "../../../../common/calendarDate";
import { esCuitValido } from "../../../../common/pages/dniUpload/cuit";
import { dniFromTaxId, normalizeName } from "../../../../common/pages/dniUpload/dniMatch";

export type CampoVerificable = "firstName" | "lastName" | "dateOfBirth" | "taxId" | "gender" | "nationality";

export const CAMPOS_VERIFICABLES: readonly CampoVerificable[] = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "taxId",
  "gender",
  "nationality",
];

export function esCampoVerificable(campo: string): campo is CampoVerificable {
  return (CAMPOS_VERIFICABLES as readonly string[]).includes(campo);
}

export interface EstadoAutor {
  dniValidatedAt: string | null;
  /** Los datos que el server validó con el DNI (la clave es lo que importa). */
  dniValidatedFields: Partial<Record<CampoVerificable, string>>;
  /** Lo guardado de esos mismos campos, normalizado para comparar (ver normalizarValor). */
  guardados: Partial<Record<CampoVerificable, string>>;
  /** El tipo de documento guardado: con él se lee el número cuando el formulario no dice el suyo. */
  tipoDeDocumento: string;
  missingFields: string[];
  withoutIpiOrDni: boolean;
}

/**
 * Copia de SOLO_DE_NOMBRE y nombreDelAutor de jupiter-server
 * (src/common/dniUpload/dniServerVerification.ts), que deciden si un nombre
 * guardado conserva su tic: el nombre normalizado (normalizeName, el espejo
 * de dniMatch.ts), o nada si tiene algo que no es de un nombre ("Pérez.",
 * "Juan Carlos 2"). Si cambia allá, cambia acá.
 */
const SOLO_DE_NOMBRE = /^[A-Za-z '\u2019-]*$/;

function nombreDelAutor(texto: unknown): string {
  const s = String(texto ?? "");
  if (!SOLO_DE_NOMBRE.test(s.normalize("NFD").replace(/[\u0300-\u036f]/g, ""))) return "";
  return normalizeName(s);
}

/**
 * Copia de documentNumberProvenByTaxId de jupiter-server
 * (src/common/dniUpload/dniServerVerification.ts), con los mismos ayudantes
 * (los espejos cuit.ts y dniMatch.ts): el número de documento que prueba el
 * número según su tipo, sin ceros a la izquierda, o nada.
 *   - du: los dígitos, si son 7 u 8.
 *   - cuitCuil: el DNI de adentro, solo si el verificador es correcto y tiene
 *     11 dígitos (uno de 10, el DNI de 7 sin su cero, queda sin probar, como
 *     en el server).
 *   - otro tipo, o sin tipo: nada; un pasaporte con los mismos dígitos no
 *     prueba un DNI.
 * Si cambia allá, cambia acá.
 */
function documentoProbado(tipo: unknown, numero: unknown): string {
  if (tipo !== "du" && tipo !== "cuitCuil") return "";
  const texto = String(numero ?? "");
  if (tipo === "cuitCuil" && !esCuitValido(texto)) return "";
  const digitos = dniFromTaxId(texto, tipo);
  if (!digitos || !/^\d{7,8}$/.test(digitos)) return "";
  return digitos.replace(/^0+/, "");
}

/**
 * Un valor de los campos verificables, en la forma en que el server lo compara
 * con el validado (verifiableValues, en dniServerVerification.ts): la fecha como
 * el día, «AAAA-MM-DD» (así la tienen el formulario y GraphQL; sin pasar por un
 * Date local, y vacía si no es un día que existe), la nacionalidad por el id de
 * la fila, el nombre y el apellido normalizados (nombreDelAutor) y el número
 * como el DNI que prueba según el tipo de documento (documentoProbado).
 */
export function normalizarValor(campo: CampoVerificable, valor: unknown, tipoDeDocumento?: unknown): string {
  if (valor === null || valor === undefined) return "";
  if (campo === "dateOfBirth") return typeof valor === "string" ? diaDeCalendario(valor) ?? "" : "";
  if (campo === "nationality") {
    return typeof valor === "object" ? String((valor as { id?: unknown }).id ?? "") : String(valor);
  }
  if (campo === "taxId") return documentoProbado(tipoDeDocumento, valor);
  if (campo === "firstName" || campo === "lastName") return nombreDelAutor(valor);
  return String(valor).trim();
}

function validadosDe(crudo: unknown): Partial<Record<CampoVerificable, string>> {
  let objeto: unknown = crudo;
  if (typeof crudo === "string") {
    try {
      objeto = JSON.parse(crudo);
    } catch {
      objeto = null;
    }
  }
  const salida: Partial<Record<CampoVerificable, string>> = {};
  if (!objeto || typeof objeto !== "object") return salida;
  for (const [clave, valor] of Object.entries(objeto as Record<string, unknown>)) {
    if (esCampoVerificable(clave)) salida[clave] = String(valor ?? "");
  }
  return salida;
}

export function armarEstados(data: any): Map<string, EstadoAutor> {
  const estados = new Map<string, EstadoAutor>();
  const filas: any[] = Array.isArray(data?.authorComposerListWithOffset) ? data.authorComposerListWithOffset : [];
  const avisos: any[] = Array.isArray(data?.authorComposerDocumentationIssues)
    ? data.authorComposerDocumentationIssues
    : [];
  const avisoDe = new Map(avisos.map((a) => [String(a.authorId), a] as const));
  for (const fila of filas) {
    if (!fila?.id) continue;
    const tipoDeDocumento = String(fila.taxIdType ?? "");
    const guardados: Partial<Record<CampoVerificable, string>> = {};
    for (const campo of CAMPOS_VERIFICABLES) guardados[campo] = normalizarValor(campo, fila[campo], tipoDeDocumento);
    const aviso = avisoDe.get(String(fila.id));
    estados.set(String(fila.id), {
      dniValidatedAt: fila.dniValidatedAt ?? null,
      dniValidatedFields: validadosDe(fila.dniValidatedFields),
      guardados,
      tipoDeDocumento,
      missingFields: Array.isArray(aviso?.missingFields) ? aviso.missingFields.map(String) : [],
      withoutIpiOrDni: !!aviso?.withoutIpiOrDni,
    });
  }
  return estados;
}

/**
 * ¿Lleva tic este campo? En la vista, si el server lo validó. En la edición,
 * además, mientras el valor siga equivaliendo al guardado con la regla del
 * server (normalizarValor): lo que se ve es lo que queda al guardar, cuando el
 * server quita el tic de lo que dejó de coincidir. En el alta, nunca.
 *
 * El número se lee con el tipo de documento del formulario (`tipoVivo`): un DU
 * que pasa a «Otro ID» sin tocar el número pierde el tic, y uno que pasa a un
 * CUIT válido con el mismo DNI lo conserva. Sin el tipo del formulario, rige
 * el guardado.
 *
 * Como keepStillVerified en el server, hace falta un valor: si lo guardado no
 * prueba nada (normalizado da vacío: «Otro ID», un CUIT con el verificador
 * mal, un nombre con un punto), no hay tic aunque lo escrito tampoco pruebe
 * nada.
 */
export function marcaVisible(
  estado: EstadoAutor | null,
  campo: CampoVerificable,
  modo: "create" | "update" | "view",
  valorVivo?: unknown,
  tipoVivo?: unknown
): boolean {
  if (!estado || modo === "create") return false;
  if (!Object.prototype.hasOwnProperty.call(estado.dniValidatedFields, campo)) return false;
  if (modo === "view") return true;
  const guardado = estado.guardados[campo] ?? "";
  if (guardado === "") return false;
  const tipo = tipoVivo === undefined ? estado.tipoDeDocumento : tipoVivo;
  return normalizarValor(campo, valorVivo, tipo) === guardado;
}

export function tieneDatosValidados(estado: EstadoAutor | null): boolean {
  return !!estado && Object.keys(estado.dniValidatedFields).length > 0;
}

const CONSULTA = `query AuthorDniStatus($ids: [String!]!, $where: AuthorComposer_Filter!, $take: Int!) {
  authorComposerDocumentationIssues(ids: $ids) { authorId missingFields withoutIpiOrDni }
  authorComposerListWithOffset(where: $where, offset: 0, take: $take) {
    id dniValidatedAt dniValidatedFields firstName lastName dateOfBirth taxIdType taxId gender nationality { id }
  }
}`;

/** El máximo de ids que acepta authorComposerDocumentationIssues (parte B). */
export const TOPE_DE_IDS = 500;

let pendientes = new Map<string, Array<(estado: EstadoAutor | null) => void>>();
let programado = false;

async function despachar(): Promise<void> {
  const lote = pendientes;
  pendientes = new Map();
  programado = false;
  const ids = Array.from(lote.keys());
  const estados = new Map<string, EstadoAutor>();
  // El server rechaza más de TOPE_DE_IDS ids por consulta (TOO_MANY_IDS).
  for (let desde = 0; desde < ids.length; desde += TOPE_DE_IDS) {
    const tramo = ids.slice(desde, desde + TOPE_DE_IDS);
    try {
      const respuesta = await restFetch("/graphql", {
        method: "POST",
        body: JSON.stringify({ query: CONSULTA, variables: { ids: tramo, where: { id: { in: tramo } }, take: tramo.length } }),
      });
      armarEstados(respuesta?.data).forEach((estado, id) => estados.set(id, estado));
    } catch {
      // Sin estado para ese tramo: sus marcas y avisos no se muestran, el resto sigue.
    }
  }
  lote.forEach((esperan, id) => esperan.forEach((resolver) => resolver(estados.get(id) ?? null)));
}

export function cargarEstadoAutor(id: string): Promise<EstadoAutor | null> {
  return new Promise((resolver) => {
    const esperan = pendientes.get(id) ?? [];
    esperan.push(resolver);
    pendientes.set(id, esperan);
    if (!programado) {
      programado = true;
      setTimeout(() => void despachar(), 0);
    }
  });
}

/**
 * El estado del autor `id`. Lo cargado se guarda junto con el id al que
 * pertenece: si el id cambia con el componente montado, hasta que llegue la
 * respuesta del nuevo se devuelve null, nunca el estado del anterior. Cada
 * carga (la inicial y las que pide el aviso de un contacto) lleva un número:
 * una respuesta que no es la última pedida se descarta.
 */
export function useEstadoAutor(id?: string): EstadoAutor | null {
  const [cargado, setCargado] = useState<{ id: string; estado: EstadoAutor | null } | null>(null);
  useEffect(() => {
    if (!id) return;
    let vigente = true;
    let ultima = 0;
    const cargar = () => {
      const numero = ++ultima;
      cargarEstadoAutor(id).then((estado) => {
        if (vigente && numero === ultima) setCargado({ id, estado });
      });
    };
    cargar();
    // Cuando un contacto confirma sus datos (aviso en vivo), se vuelve a mirar,
    // pero solo si fue un autor y, cuando el aviso dice cuál, este.
    const alConfirmar = (evento: Event) => {
      const detalle = (evento as CustomEvent<{ entityName?: unknown; recordId?: unknown } | null>).detail;
      const entidad = detalle?.entityName;
      if (entidad !== undefined && entidad !== null && entidad !== "AuthorComposer") return;
      const registro = detalle?.recordId;
      if (typeof registro === "string" && registro !== id) return;
      cargar();
    };
    window.addEventListener("externalValidationSubmitted", alConfirmar);
    return () => {
      vigente = false;
      window.removeEventListener("externalValidationSubmitted", alConfirmar);
    };
  }, [id]);
  return id && cargado?.id === id ? cargado.estado : null;
}
